import {
  ChangeDetectorRef,
  Component,
  computed,
  contentChildren,
  EventEmitter,
  inject,
  input,
  Input,
  Output
} from "@angular/core";
import { ButtonClickAsyncEvent } from "src/app/components/ui/button/button-click-async-event";
import { Lambda } from "src/app/support/lambda";
import { ActionComponent } from "src/app/components/ui/actions/action.component";
import { DxDropDownButtonTypes } from "devextreme-angular/ui/drop-down-button";
import { ClickEvent } from "devextreme/ui/button";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("ButtonComponent");
type ButtonLayout = "default" | "top-large-icon" | "ribbon";

export type ButtonType =
  | "primary"
  | "primary-outlined"
  | "default"
  | "text"
  | "outlined"
  | "normal"
  | "secondary-outlined"
  | "jumbo-snoots-plus";

@Component({
  standalone: false,
  selector: "snt-button",
  templateUrl: "button.component.html",
  styles: `
    :host {
      display: flex;
    }
  `
})
export class ButtonComponent {
  useDx = input<boolean>(false);
  stylingMode = input<string>("contained");
  dxFlex = input<boolean>(false);

  actions = contentChildren(ActionComponent);

  protected readonly dxElementAttr = computed<
    Record<string, string> | undefined
  >(() => {
    const trackEvent: string | undefined = this.trackEvent();

    if (!trackEvent) {
      return undefined;
    }

    return { "data-track": trackEvent };
  });

  splitActions = computed(() => {
    const actions = this.actions();
    if (!actions) {
      return undefined;
    }

    return actions.map((action, index) => {
      return {
        index,
        text: action.title,
        disabled: action.disabled,
        visible: true
      };
    });
  });

  @Input()
  layout: ButtonLayout = "default";

  @Input()
  icon: string | undefined = "";

  @Input()
  iconStack: string | undefined;

  @Input()
  title: string | undefined = "";

  @Input()
  underline: boolean = false;

  @Input()
  type: ButtonType | undefined = "default";

  @Input()
  html: boolean = false;

  @Input()
  disabled: boolean = false;

  @Input()
  loading: boolean = false;

  @Input()
  small: boolean = false;

  @Input()
  split: boolean = false;

  @Input()
  splitTooltip: string | undefined;

  @Input()
  textAlign: "start" | "middle" | "end" = "middle";

  @Input()
  noRoundedRight: boolean = false;

  @Input()
  noRoundedLeft: boolean = false;

  trackEvent = input<string | undefined>(undefined);

  @Input()
  textClass: string | undefined;

  @Input()
  bgClass: string | undefined;

  @Input()
  hoverBgClass: string | undefined;

  @Input()
  iconColor: string | undefined;

  @Output()
  onClick = new EventEmitter<void>();

  @Output()
  onClickAsync = new EventEmitter();

  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  clickAsyncEvent: ButtonClickAsyncEvent = {
    lock: () => {
      this.loading = true;
      this.disabled = true;
    },
    unclock: () => {
      this.loading = false;
      this.disabled = false;
    },
    execute: async (fn: Lambda<Promise<void>>) => {
      try {
        this.clickAsyncEvent.lock();
        await fn();
      } catch (ex) {
        logger.error(ex);
      } finally {
        this.clickAsyncEvent.unclock();
        this.changeDetectorRef.detectChanges();
      }
    }
  };

  onDxClick(dxClickEvent: ClickEvent): void {
    dxClickEvent.event?.stopImmediatePropagation();
    if (this.disabled) {
      return;
    }

    this.onClick.emit();
    this.onClickAsync.emit(this.clickAsyncEvent);
  }

  onClickButton(clickEvent: MouseEvent): void {
    clickEvent.stopImmediatePropagation();
    if (this.disabled) {
      return;
    }

    this.onClick.emit();
    this.onClickAsync.emit(this.clickAsyncEvent);
  }

  onSplitItemClick(clickEvent: DxDropDownButtonTypes.ItemClickEvent): void {
    this.actions()?.at(clickEvent.itemData!.index)?.do.emit();
  }
}
